import { canAffordColor, cannotAffordColor } from '@/common/types/game';
import { Customization, EquipmentConfiguration } from '@/common/types/student';
import Customizations from '@/game/GameComponents/GameData/customizations.json';
import { EventBus } from '@/game/GameComponents/PhaserGame/EventBus';
import { getCharacterInventory } from '@/game/GameComponents/PhaserGame/GameInstance';
import EventsManager from '@/game/GameComponents/PhaserGame/Managers/EventsManager';
import { ResponsivenessManager } from '@/game/GameComponents/PhaserGame/Managers/ResponsivenessManager';
import { getSceneManager } from '@/game/GameComponents/PhaserGame/Managers/SceneManager';
import CosmeticShop from '@/game/GameComponents/PhaserGame/Scenes/CosmeticShop/CosmeticShop';
import {
  getIconPath,
  getItemRarityTier,
  getPriceForItem,
} from '@/game/GameComponents/PhaserGame/Utils/CosmeticUtils';
import GAME_EVENTS from '@/game/GameComponents/PhaserGame/Utils/gameEventsDef';
import { ItemTabData } from '@/game/GameComponents/PhaserGame/Utils/ItemTabData';
import { GridLayoutGroup } from '@/game/GameComponents/PhaserGame/Utils/PhaserLayout/GridLayoutGroup';
import { ItemScrollGroup } from '@/game/GameComponents/PhaserGame/Utils/PhaserLayout/ItemScrollGroup';
import {
  getAnchorPlugin,
  setBitmapColor,
  waitForSeconds,
} from '@/game/GameComponents/PhaserGame/Utils/PhaserUtils';
import { DEFAULT_CURSOR_PATH } from '@/game/GameComponents/Utils/GameSpriteAssetsMap';
import PurchaseNotification from '@/game/prefabs/CosmeticShop/PurchaseNotification';
import ShopGridItem from '@/game/prefabs/CosmeticShop/ShopGridItem';
import ShopTab from '@/game/prefabs/CosmeticShop/ShopTab';
import ShopTab_m from '@/game/prefabs/CosmeticShop/ShopTab_m';
import DefaultButton from '@/game/prefabs/GeneralUI/Buttons/DefaultButton';
import DefaultDenyButton from '@/game/prefabs/GeneralUI/Buttons/DefaultDenyButton';
import HUDButton from '@/game/prefabs/GeneralUI/Buttons/HUDButton';
import PlayerDoll from '@/game/prefabs/GeneralUI/PlayerDoll';

export class CosmeticShopController {
  constructor(private shop: CosmeticShop) {
    this.customizations = Customizations as Customization[];
    this.create();
  }

  currentTab: string = '';
  customizations?: Customization[];
  tabs: ShopTab[] = [];
  tabs_m: ShopTab_m[] = [];

  previews: Customization[] = [];
  currentCustomization?: Customization;
  itemScrollGroup?: ItemScrollGroup;
  itemScrollGroup_m?: ItemScrollGroup;

  hasInitialized = false;

  preload() {}

  init(data: any) {
    this.hasInitialized = data.initialized || false;
  }

  getResponsiveElements(key: keyof CosmeticShop) {
    return ResponsivenessManager.GetAllResponsiveComponentsFromScene<CosmeticShop>(
      this.shop,
      key,
    );
  }

  async create() {
    if (this.hasInitialized) return;
    const { shop } = this;

    shop.scene.bringToTop();

    // Set up the tab interactivity.
    const tabLayoutGroup = new GridLayoutGroup({
      scene: shop,
      elements: [],
      columns: 1,
      hPadding: 0,
      vPadding: 60,
    });
    shop.shopTabs.add(tabLayoutGroup);

    // Mobile tabs.
    const tabLayoutGroup_m = new GridLayoutGroup({
      scene: shop,
      elements: [],
      columns: 8,
      hPadding: 52,
      vPadding: 0,
    });
    shop.shopTabs_m.add(tabLayoutGroup_m);

    const tabData = ItemTabData;
    for (let t in tabData) {
      // Desktop tab.
      const newTab = new ShopTab(shop, 0, 0);
      newTab.controller.onUserSelectedTab.push((tab: ShopTab | ShopTab_m) => {
        this.handleUserClickedTab(tab);
      });
      newTab.controller.setTabData(tabData[t]);
      shop.add.existing(newTab);
      this.tabs?.push(newTab);

      tabLayoutGroup.addItem(newTab);

      // Mobile Tab.
      const newTab_m = new ShopTab_m(shop, 0, 0);
      newTab_m.controller.onUserSelectedTab.push((tab: ShopTab | ShopTab_m) => {
        this.handleUserClickedTab(tab);
      });
      newTab_m.controller.setTabData(tabData[t]);
      shop.add.existing(newTab_m);
      newTab_m.controller.joinToTab(newTab);
      this.tabs_m?.push(newTab_m);

      tabLayoutGroup_m.addItem(newTab_m);
    }

    // Set up the item view modal interactivity.
    // this.getResponsiveElements('itemViewCancelButton').forEach(
    //   (itemViewCancelButton: DefaultDenyButton) => {
    //     itemViewCancelButton.controller.setSubmissionCallback(() => {
    //       // Close all item view modals.
    //       this.getResponsiveElements('itemViewModal').forEach(
    //         (itemViewModal: Phaser.GameObjects.Container) => {
    //           itemViewModal.setVisible(false);
    //         },
    //       );
    //       this.currentCustomization = undefined;
    //     });
    //   },
    // );

    // Set up item purchasing.
    this.getResponsiveElements('itemViewAcceptButton').forEach(
      (itemViewAcceptButton: DefaultButton) => {
        itemViewAcceptButton.controller.setSubmissionCallback(() => {
          this.getResponsiveElements('itemViewModal').forEach(
            (itemViewModal: Phaser.GameObjects.Container) => {
              itemViewModal.setVisible(false);
            },
          );
          if (this.currentCustomization) {
            this.attemptPurchaseItem(this.currentCustomization);
          }
        });
      },
    );

    function closeShop() {
      getSceneManager()?.pauseScene('CosmeticShop');
      EventBus.emit(GAME_EVENTS.COSMETIC_SHOP_CLOSED);
    }

    this.getResponsiveElements('leaveButton').forEach(
      (leaveButton: DefaultDenyButton) => {
        leaveButton.controller.setSubmissionCallback(() => {
          closeShop();
        });
      },
    );

    this.getResponsiveElements('closeButton').forEach(
      (closeButton: HUDButton) => {
        closeButton.submitSFX = 'panel-close';
        closeButton.setSubmissionCallback(() => {
          closeShop();
        });
      },
    );

    this.getResponsiveElements('closeButton_1').forEach(
      (closeButton: HUDButton) => {
        closeButton.setSubmissionCallback(() => {
          // Close all item view modals.
          this.getResponsiveElements('itemViewModal').forEach(
            (itemViewModal: Phaser.GameObjects.Container) => {
              itemViewModal.setVisible(false);
            },
          );
        });
      },
    );

    // Desktop Item Scroll Group.
    this.itemScrollGroup = new ItemScrollGroup({
      scene: shop,
      content: shop.content,
      gridBacking: shop.gridBacking,
      scrollBacking: shop.scrollBarBacking,
      scrollHandle: shop.scrollBarHandle,
      loadingContainer: shop.loadingContainer,
      loadingSpinner: shop.loadingSpinner,
      showItemPrices: true,
    });

    // Mobile Item Scroll Group.
    this.itemScrollGroup_m = new ItemScrollGroup({
      scene: shop,
      content: shop.content_m,
      gridBacking: shop.gridBacking_m,
      scrollBacking: shop.scrollBarBacking_m,
      scrollHandle: shop.scrollBarHandle_m,
      loadingContainer: shop.loadingContainer_m,
      loadingSpinner: shop.loadingSpinner_m,
      showItemPrices: true,
      gridLayoutConfig: {
        scene: shop,
        elements: [],
        columns: 8,
        hPadding: 68,
        vPadding: 98,
        hMargin: 8,
        vMargin: 8,
        backingElement: shop.gridBacking,
      },
    });

    const characterInventory = getCharacterInventory();
    const initialFilter = (c: Customization) =>
      !characterInventory.unlockedItems.includes(c.id) && c.slot === 'hats';

    await waitForSeconds(shop.time, 0.1);

    const onUserClickedShopItem = (item: ShopGridItem) => {
      this.handleUserClickedShopItem(item);
      this.updateSelections();
    };

    this.itemScrollGroup?.initialize(onUserClickedShopItem, initialFilter);
    this.itemScrollGroup_m?.initialize(onUserClickedShopItem, initialFilter);

    // Hide the item view modals.
    this.getResponsiveElements('itemViewModal').forEach(
      (itemViewModal: Phaser.GameObjects.Container) => {
        itemViewModal.setVisible(false);
      },
    );

    // Make the backing responsive to block input through the item view modal.
    this.getResponsiveElements('itemViewBacking').forEach(
      (itemViewBacking: Phaser.GameObjects.Image) => {
        itemViewBacking.setInteractive({
          cursor: `${DEFAULT_CURSOR_PATH}`,
        });
      },
    );

    // Ensure spinner blocker blocks input.
    this.getResponsiveElements('spinnerBlocker').forEach(
      (spinnerBlocker: Phaser.GameObjects.Image) => {
        spinnerBlocker.setInteractive({
          cursor: `${DEFAULT_CURSOR_PATH}`,
        });
      },
    );

    this.hideAllItemsBought();

    if (this.tabs) {
      this.selectTab(this.tabs[0]);
    }

    this.initializeResponsiveness();
    this.itemScrollGroup?.updateInteractivityWindows();
    this.itemScrollGroup_m?.updateInteractivityWindows();

    EventsManager.registerEventOn(
      shop,
      GAME_EVENTS.COIN_VALUE_CHANGED,
      this.updateRubies,
      this,
    );
    this.updateRubies();
  }

  refresh() {
    // Get all the cosmetics to display for the current tab.
    const { customizations } = this;
    if (!customizations) return;
    const { shop } = this;

    const characterInventory = getCharacterInventory();

    shop.categoryHeaderText.setText(this.currentTab);

    const currSlot = ItemTabData.find((t) => t.name === this.currentTab)?.slot;

    const itemFilter = (c: Customization) =>
      c.slot === currSlot &&
      !c.disable &&
      characterInventory.unlockedItems.find((i: number) => i === c.id) ===
        undefined;

    const onUpdateFinished = () => {
      this.updateAllItemsBought();
    };

    this.itemScrollGroup?.setItemFilter(itemFilter, onUpdateFinished);
    this.itemScrollGroup_m?.setItemFilter(itemFilter, onUpdateFinished);

    // rubies
    this.updateRubies();
    this.updateSelections();

    if (this.itemScrollGroup?.isLoading) {
      this.hideAllItemsBought();
    } else {
      this.updateAllItemsBought();
    }
  }

  updateSelections() {
    const equippedItems = getCharacterInventory().equippedItems;
    // Set selected item to be highlighted.
    this.itemScrollGroup?.items.forEach((item) => {
      item.setIsSelected(false);
      if (
        Object.values(equippedItems).find(
          (i: number) => i === item.cosmetic?.id,
        ) !== undefined
      ) {
        item.setIsSelected(true);
      }
    });
  }

  updateRubies() {
    this.getResponsiveElements('playerRubiesText').forEach(
      (playerRubiesText: Phaser.GameObjects.BitmapText) => {
        playerRubiesText.setText(getCharacterInventory().coins.toString());
      },
    );
  }

  hideAllItemsBought() {
    this.getResponsiveElements('allItemsBoughtContainer').forEach(
      (allItemsBoughtContainer: Phaser.GameObjects.Container) => {
        allItemsBoughtContainer.setVisible(false);
      },
    );
  }

  updateAllItemsBought() {
    this.getResponsiveElements('allItemsBoughtContainer').forEach(
      (allItemsBoughtContainer: Phaser.GameObjects.Container) => {
        const visibleItems = this.itemScrollGroup?.items.filter(
          (item) => item.visible,
        );
        allItemsBoughtContainer.setVisible(
          visibleItems !== undefined && visibleItems.length === 0,
        );
      },
    );
  }

  getAllTabs() {
    return [...this.tabs, ...this.tabs_m];
  }

  selectTab(tab: ShopTab | ShopTab_m) {
    this.getAllTabs().forEach((t: ShopTab | ShopTab_m) => {
      t.controller.setSelected(false);
    });
    tab.controller.setSelected(true);
    tab.controller.jointTab?.controller.setSelected(true);
    this.currentTab = tab.controller.tabData?.name || '';
    this.getResponsiveElements('categoryHeaderText').forEach(
      (headerText: Phaser.GameObjects.BitmapText) => {
        headerText.setText(this.currentTab);
      },
    );
  }

  handleUserClickedTab(tab: ShopTab | ShopTab_m) {
    this.selectTab(tab);
    this.refresh();
  }

  handleUserClickedShopItem(item: ShopGridItem) {
    if (!item.cosmetic) return;

    // Open the item view modal.
    this.getResponsiveElements('itemViewModal').forEach(
      (itemViewModal: Phaser.GameObjects.Container) => {
        itemViewModal.setVisible(true);
      },
    );

    this.setItemViewModalItem(item.cosmetic);
  }

  setItemViewModalItem(cosmetic: Customization) {
    this.currentCustomization = cosmetic;

    // Set item price.
    const price = getPriceForItem(cosmetic.id);
    this.getResponsiveElements('itemPriceText').forEach(
      (itemPriceText: Phaser.GameObjects.BitmapText) => {
        itemPriceText.setText(price.toString() || '0');
      },
    );

    // Set rarity text label and colour.
    const itemTier = getItemRarityTier(cosmetic.id);
    this.getResponsiveElements('rarityText').forEach(
      (rarityText: Phaser.GameObjects.BitmapText) => {
        rarityText.setText(itemTier?.name || '');
        rarityText.setTint(parseInt(itemTier?.color || '0xffffff'));
      },
    );

    // Set item frame tint based on item rarity.
    const tier = getItemRarityTier(cosmetic.id);
    if (tier) {
      this.getResponsiveElements('itemViewRarityFrame').forEach(
        (itemViewRarityFrame: Phaser.GameObjects.NineSlice) => {
          itemViewRarityFrame.setTint(parseInt(tier?.color) || 0xffffff);
        },
      );
    }

    // Set item icon.
    this.getResponsiveElements('itemView').forEach(
      (itemView: Phaser.GameObjects.Image) => {
        itemView.setTexture(getIconPath(cosmetic));
      },
    );

    // Set item name.
    this.getResponsiveElements('itemViewItemName').forEach(
      (itemViewItemName: Phaser.GameObjects.BitmapText) => {
        itemViewItemName.setText(cosmetic.name);
      },
    );

    // Set rubies text.
    const canAfford = getCharacterInventory().coins >= price;
    this.getResponsiveElements('playerRubiesText_1').forEach(
      (playerRubiesText: Phaser.GameObjects.BitmapText) => {
        playerRubiesText.setText(getCharacterInventory().coins.toString());
        setBitmapColor(
          playerRubiesText,
          canAfford ? canAffordColor : cannotAffordColor,
        );
      },
    );

    // Set accept button interactivity based on whether or not the
    // player can afford the item.
    this.getResponsiveElements('itemViewAcceptButton').forEach(
      (itemViewAcceptButton: DefaultButton) => {
        itemViewAcceptButton.controller.makeInteractive(canAfford);
      },
    );

    // Update player doll cosmetic overrides to show the new item on the doll.
    let newOverrides: Partial<EquipmentConfiguration> = {};
    newOverrides[`${cosmetic.slot as keyof EquipmentConfiguration}`] =
      cosmetic.id;
    this.getResponsiveElements('playerDoll').forEach(
      (playerDoll: PlayerDoll) => {
        playerDoll.setCosmeticOverrides(newOverrides);
      },
    );
  }

  addItemToPreview(customization?: Customization) {
    if (!customization) return;
    const existingCustomization = this.previews.find(
      (p) => p.id === customization.id,
    );
    if (existingCustomization) return;
    this.previews.push(customization);
  }

  attemptPurchaseItem(item: Customization) {
    const playerMoney = getCharacterInventory().coins;
    const price = getPriceForItem(item.id);
    if (playerMoney >= price) {
      // Complete the purchase.
      EventBus.emit(GAME_EVENTS.PLAYER_PURCHASED_COSMETIC, item);

      // Show some success feedback.
      this.getResponsiveElements('purchaseNotification').forEach(
        (purchaseNotificationContainer: PurchaseNotification) => {
          purchaseNotificationContainer.showPurchaseNotification(item);
        },
      );
    }

    this.refresh();
  }

  initializeResponsiveness() {
    const { shop } = this;

    // Breakpoints.
    ResponsivenessManager.InitializeResponsiveBreakpoints({
      scene: shop,
      desktopView: shop.desktopView,
      mobileView: shop.mobileView,
    });

    // Responsiveness.
    const anchor = getAnchorPlugin(shop);
    anchor.add(shop.mainContainer, {
      centerX: '50%',
      centerY: '50%',
    });
    anchor.add(shop.purchaseNotificationOrigin, {
      left: '100%',
    });
    anchor.add(shop.itemViewBacking, {
      centerX: '0%',
      centerY: '0%',
      width: '200%',
      height: '200%',
    });
    anchor.add(shop.itemViewModal, {
      centerX: '50%',
      centerY: '50%',
    });

    anchor.add(shop.mainContainer_m, {
      centerX: '50%',
      centerY: '50%',
    });

    anchor.add(shop.purchaseNotificationOrigin_m, {
      left: '100%',
    });
    anchor.add(shop.itemViewModal_m, {
      centerX: '50%',
      centerY: '50%',
    });

    ResponsivenessManager.MakeElementScaleDownWithGameSize(shop.mainContainer);
    ResponsivenessManager.MakeElementScaleDownWithGameSize(shop.itemViewModal);
    ResponsivenessManager.MakeElementScaleDownWithGameSize(
      shop.itemViewModal_m,
    );
  }
}
